import React from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faUserMd, 
  faChalkboardTeacher, 
  faMicroscope, 
  faCertificate, 
  faHeartPulse 
} from "@fortawesome/free-solid-svg-icons";
import NavbarLogo from "../../components/Navbar/csdv2Logo.png";
import cmeBg from "../../assets/education/cme_bg.png";

const EducationCME = () => {
  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* Hero Section */}
      <section className="relative py-32 gradient-bg text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10 bg-white" />
        <div className="container mx-auto px-6 text-center relative z-10">
          <h1 className="text-4xl md:text-7xl font-black mb-6 tracking-tighter flex flex-col items-center justify-center gap-6">
            <img src={NavbarLogo} alt="Logo" className="hidden md:block md:h-28 w-auto mb-4" />
            <span>Education & <span className="italic text-brand-beige">CME Programs</span></span>
          </h1>
          <p className="text-xl md:text-2xl max-w-4xl mx-auto font-light opacity-95 leading-relaxed">
            Empowering the medical community with state-of-the-art knowledge and practices in pediatric cardiology.
          </p>
        </div>
      </section>

      {/* Intro & Main Image Section */}
      <section className="py-24 bg-white overflow-hidden">
        <div className="container mx-auto px-6">
          <div className="flex flex-col lg:flex-row gap-16 items-center">
            <div className="lg:w-1/2 relative group">
              <div className="absolute -inset-6 bg-brand-red/10 rounded-[3rem] blur-2xl group-hover:bg-brand-red/20 transition-all duration-500" />
              <img
                src={cmeBg}
                alt="Medical Education Seminar"
                className="rounded-[2rem] shadow-2xl relative z-10 border-8 border-white object-cover transform hover:scale-[1.02] transition-transform duration-700"
              />
            </div>
            <div className="lg:w-1/2">
              <h2 className="text-4xl font-black text-slate-900 mb-6 tracking-tighter">
                Fostering Excellence in <span className="text-brand-red">Pediatric Cardiac Care</span>
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Our Continuing Medical Education (CME) programs are designed to keep healthcare professionals at the forefront of pediatric cardiology. By bridging the gap between emerging research and clinical practice, we ensure that every child gets world-class care, no matter where they are treated.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[
                  { icon: faChalkboardTeacher, title: "Interactive Seminars", desc: "Expert-led sessions." },
                  { icon: faMicroscope, title: "Clinical Research", desc: "Latest advancements." },
                  { icon: faUserMd, title: "Hands-on Training", desc: "Practical workshops." },
                  { icon: faCertificate, title: "Certifications", desc: "Accredited programs." },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4 p-4 rounded-xl hover:bg-slate-50 transition-colors">
                    <div className="flex-none w-12 h-12 rounded-full bg-brand-red/10 text-brand-red flex items-center justify-center text-xl shadow-inner">
                      <FontAwesomeIcon icon={item.icon} />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-800">{item.title}</h4>
                      <p className="text-sm text-slate-500">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Program Highlights */}
      <section className="py-24 bg-slate-50 relative z-20">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black text-slate-900 tracking-tight uppercase">Program Highlights</h2>
            <div className="w-24 h-1 bg-brand-red mx-auto mt-6 rounded-full"></div>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="p-10 bg-white rounded-3xl shadow-xl hover:-translate-y-2 transition-transform duration-300 border-t-4 border-brand-red">
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Masterclasses</h3>
              <p className="text-slate-600 leading-relaxed">
                In-depth sessions led by renowned pediatric cardiologists, focusing on complex congenital heart defects, innovative echo techniques, and advanced surgical interventions.
              </p>
            </div>
            <div className="p-10 bg-white rounded-3xl shadow-xl hover:-translate-y-2 transition-transform duration-300 border-t-4 border-brand-red">
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Skill Workshops</h3>
              <p className="text-slate-600 leading-relaxed">
                Hands-on simulation training for pediatric intensive care, neonatal life support, and post-operative monitoring to improve survival rates and recovery quality.
              </p>
            </div>
            <div className="p-10 bg-white rounded-3xl shadow-xl hover:-translate-y-2 transition-transform duration-300 border-t-4 border-brand-red">
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Rural Outreach Training</h3>
              <p className="text-slate-600 leading-relaxed">
                Equipping primary healthcare workers to identify early signs of CHD in rural communities and guiding them on timely referral pathways.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-32 bg-slate-900 text-white text-center">
        <div className="container mx-auto px-6">
          <div className="w-24 h-24 gradient-bg rounded-3xl flex items-center justify-center text-white mx-auto shadow-2xl mb-12">
            <FontAwesomeIcon icon={faHeartPulse} size="3x" className="animate-pulse" />
          </div>
          <h2 className="text-4xl md:text-5xl font-black tracking-tighter mb-8 max-w-3xl mx-auto leading-tight">
            Interested in our upcoming <span className="text-brand-red">CME Programs?</span>
          </h2>
          <p className="text-lg text-slate-400 mb-10 max-w-2xl mx-auto">
            Get in touch to learn more about registration, upcoming dates, and guest speakers.
          </p>
          <Link to="/contactus" className="inline-block px-12 py-5 gradient-bg text-white rounded-full font-black text-xl hover-glow transform hover:scale-105 transition-all shadow-2xl uppercase tracking-tighter">
            Contact Us
          </Link>
        </div>
      </section>
    </div>
  );
};

export default EducationCME;
